// Every user-visible string the design system owns itself (i.e. not already passed in by props).
// The package stays app-agnostic: it ships English defaults and lets the host translate them by
// mounting <AdminUiProvider labels={...}>. Without a provider every component still works in English.
//
// This module is shared by the main entry and the `./rich-text-editor` subpath entry, so both read
// the same context instance. It must stay free of Tiptap imports.

import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { RICH_TEXT_EDITOR_COPY, type RichTextEditorLabels } from './rich-text/copy'

export interface ImageCropLabels {
  title: string
  hint: string
  zoom: string
  confirm: string
  saving: string
  cancel: string
  failed: string
}

export interface AdminUiLabels {
  /** aria-label of close (X) buttons: Modal, toasts. */
  close: string
  /** Default confirm / cancel buttons of useConfirm(). */
  confirm: string
  cancel: string
  /** StickySaveBar. */
  unsavedChangesRegion: string
  unsavedChanges: string
  discard: string
  saveChanges: string
  saving: string
  /** Default aria-label of <Breadcrumb>. */
  breadcrumb: string
  /** <Table error={...}>: `${tableLoadError} ${message}`; the reload hint is used when the error has no message. */
  tableLoadError: string
  tableReload: string
  /** <SearchSelect> defaults when the host passes none. */
  select: string
  searchPlaceholder: string
  noResults: string
  /** <FileUploadButton> default text. */
  chooseFile: string
  /** Defaults for <ImageCropDialog>; hosts usually override title/hint per use. */
  imageCrop: ImageCropLabels
  /** Defaults for <RichTextEditor>; a per-field `label` prop still wins. */
  richText: RichTextEditorLabels
  /** BCP 47 locale for <NumberField> parsing and grouping. */
  numberLocale: string
}

export const DEFAULT_ADMIN_UI_LABELS: AdminUiLabels = {
  close: 'Close',
  confirm: 'Confirm',
  cancel: 'Cancel',
  unsavedChangesRegion: 'Unsaved changes',
  unsavedChanges: 'You have unsaved changes',
  discard: 'Discard',
  saveChanges: 'Save changes',
  saving: 'Saving…',
  breadcrumb: 'Breadcrumb',
  tableLoadError: 'Could not load data.',
  tableReload: 'Reload the page to try again.',
  select: 'Select…',
  searchPlaceholder: 'Search…',
  noResults: 'No results',
  chooseFile: 'Choose a file',
  imageCrop: {
    title: 'Crop image',
    hint: 'Drag to reposition. Use the slider or pinch to zoom.',
    zoom: 'Zoom',
    confirm: 'Crop and use',
    saving: 'Saving…',
    cancel: 'Cancel',
    failed: 'This image could not be read. Try another file.',
  },
  richText: RICH_TEXT_EDITOR_COPY,
  numberLocale: 'id-ID',
}

/** What a host may pass: any subset, with the two nested groups merged key by key. */
export type AdminUiLabelsInput = Partial<Omit<AdminUiLabels, 'imageCrop' | 'richText'>> & {
  imageCrop?: Partial<ImageCropLabels> | undefined
  richText?: Partial<RichTextEditorLabels> | undefined
}

function mergeLabels(input: AdminUiLabelsInput | undefined): AdminUiLabels {
  if (!input) return DEFAULT_ADMIN_UI_LABELS
  const { imageCrop, richText, ...flat } = input
  return {
    ...DEFAULT_ADMIN_UI_LABELS,
    ...(flat as Partial<AdminUiLabels>),
    imageCrop: { ...DEFAULT_ADMIN_UI_LABELS.imageCrop, ...imageCrop },
    richText: { ...DEFAULT_ADMIN_UI_LABELS.richText, ...richText },
  }
}

const AdminUiLabelsContext = createContext<AdminUiLabels>(DEFAULT_ADMIN_UI_LABELS)

export function AdminUiProvider({ labels, children }: { labels?: AdminUiLabelsInput | undefined; children: ReactNode }) {
  const value = useMemo(() => mergeLabels(labels), [labels])
  return <AdminUiLabelsContext.Provider value={value}>{children}</AdminUiLabelsContext.Provider>
}

/** The active labels; English defaults when no <AdminUiProvider> is mounted. */
export function useAdminUiLabels(): AdminUiLabels {
  return useContext(AdminUiLabelsContext)
}
